'use client';

import type { CSSProperties, JSX, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import {
  buildMeterDots,
  computeArtboardScale,
  DEBATE_STARTS_ARTBOARD,
  DEBATE_STARTS_BACKGROUND_SRC,
  DEBATE_STARTS_CHIPS,
  DEBATE_STARTS_METER,
  DEBATE_STARTS_OUTCOMES,
  DEBATE_STARTS_TIMING,
  type DebateStartsMeterDot,
  easeOutCubic,
  meterFilledColumns,
  meterValuesForFraction,
} from '@/components/support-mainnet/how-it-works/debate-starts-animation-engine';
import {
  useHowItWorksOrchestrationActive,
  useHowItWorksPlaybackGeneration,
  useHowItWorksPlaybackInstanceKey,
  useHowItWorksStepShouldPlay,
} from '@/components/support-mainnet/how-it-works/how-it-works-step-playback-api';
import { cn } from '@/lib/ui/utils';

const METER_DOTS: DebateStartsMeterDot[] = buildMeterDots();
const CHIP_COUNT: number = DEBATE_STARTS_CHIPS.length;
const OUTCOME_COUNT: number = DEBATE_STARTS_OUTCOMES.length;

type DebateStartsPlaybackState = {
  go: boolean;
  isCard: boolean;
  isCollapsed: boolean;
  visibleChips: boolean[];
  visibleOutcomes: boolean[];
  meterFrac: number;
  amountEur: number;
  pct: number;
  users: number;
  surfaceOpaque: boolean;
};

function createInitialPlaybackState(): DebateStartsPlaybackState {
  return {
    go: false,
    isCard: false,
    isCollapsed: false,
    visibleChips: Array.from({ length: CHIP_COUNT }, () => false),
    visibleOutcomes: Array.from({ length: OUTCOME_COUNT }, () => false),
    meterFrac: 0,
    amountEur: 0,
    pct: 0,
    users: 0,
    surfaceOpaque: false,
  };
}

function createFinalPlaybackState(): DebateStartsPlaybackState {
  return {
    go: false,
    isCard: true,
    isCollapsed: true,
    visibleChips: Array.from({ length: CHIP_COUNT }, () => false),
    visibleOutcomes: Array.from({ length: OUTCOME_COUNT }, () => true),
    meterFrac: DEBATE_STARTS_TIMING.meterTargetFrac,
    amountEur: DEBATE_STARTS_TIMING.finalAmountEur,
    pct: DEBATE_STARTS_TIMING.finalPct,
    users: DEBATE_STARTS_TIMING.finalUsers,
    surfaceOpaque: true,
  };
}

function useDebateStartsPlayback(
  rootRef: RefObject<HTMLDivElement | null>,
): DebateStartsPlaybackState {
  const [state, setState] = useState(createInitialPlaybackState);
  const playedRef = useRef(false);
  const orchestrationActive = useHowItWorksOrchestrationActive();
  const shouldPlay = useHowItWorksStepShouldPlay(0);
  const playbackGeneration = useHowItWorksPlaybackGeneration(0);
  const playbackEnabled = orchestrationActive === false || shouldPlay;

  useEffect(() => {
    void playbackGeneration;
    if (!playbackEnabled) {
      return;
    }

    const root = rootRef.current;
    if (root === null) {
      return;
    }

    playedRef.current = false;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let timerIds: number[] = [];
    let meterFrameId: number | null = null;

    const at = (delayMs: number, run: () => void): void => {
      timerIds = [...timerIds, window.setTimeout(run, delayMs)];
    };

    const cleanup = (): void => {
      for (const timer of timerIds) {
        window.clearTimeout(timer);
      }
      timerIds = [];
      if (meterFrameId !== null) {
        window.cancelAnimationFrame(meterFrameId);
        meterFrameId = null;
      }
    };

    const tweenMeter = (durationMs: number): void => {
      const startedAt = performance.now();
      const frame = (now: number): void => {
        const progress = Math.min((now - startedAt) / durationMs, 1);
        const eased = easeOutCubic(progress);
        const values = meterValuesForFraction(eased);
        setState((previous) => ({ ...previous, ...values }));
        if (progress < 1) {
          meterFrameId = window.requestAnimationFrame(frame);
          return;
        }
        meterFrameId = null;
      };
      meterFrameId = window.requestAnimationFrame(frame);
    };

    const play = (): void => {
      if (playedRef.current) {
        return;
      }
      playedRef.current = true;

      if (reducedMotion) {
        setState(createFinalPlaybackState());
        return;
      }

      setState(createInitialPlaybackState());

      at(DEBATE_STARTS_TIMING.morphToCardMs, () => {
        setState((previous) => ({ ...previous, isCard: true }));
      });

      at(DEBATE_STARTS_TIMING.meterStartMs, () => {
        tweenMeter(DEBATE_STARTS_TIMING.meterDurationMs);
      });

      for (let index = 0; index < CHIP_COUNT; index += 1) {
        at(DEBATE_STARTS_TIMING.chipBaseMs + index * DEBATE_STARTS_TIMING.chipStaggerMs, () => {
          setState((previous) => {
            const visibleChips = [...previous.visibleChips];
            visibleChips[index] = true;
            return { ...previous, visibleChips };
          });
        });
      }

      at(DEBATE_STARTS_TIMING.collapseMs, () => {
        setState((previous) => ({
          ...previous,
          isCollapsed: true,
          visibleChips: Array.from({ length: CHIP_COUNT }, () => false),
        }));
      });

      for (let index = 0; index < OUTCOME_COUNT; index += 1) {
        at(
          DEBATE_STARTS_TIMING.outcomeBaseMs + index * DEBATE_STARTS_TIMING.outcomeStaggerMs,
          () => {
            setState((previous) => {
              const visibleOutcomes = [...previous.visibleOutcomes];
              visibleOutcomes[index] = true;
              return { ...previous, visibleOutcomes };
            });
          },
        );
      }

      at(0, () => {
        setState((previous) => ({ ...previous, go: true }));
      });
    };

    if (orchestrationActive) {
      const startTimer = window.setTimeout(() => {
        play();
      }, 0);
      return (): void => {
        window.clearTimeout(startTimer);
        cleanup();
      };
    }

    if (typeof IntersectionObserver === 'undefined') {
      const startTimer = window.setTimeout(() => {
        play();
      }, 0);
      return (): void => {
        window.clearTimeout(startTimer);
        cleanup();
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === root && entry.isIntersecting) {
            play();
          }
        }
      },
      { threshold: DEBATE_STARTS_TIMING.intersectionThreshold },
    );

    observer.observe(root);

    return (): void => {
      observer.disconnect();
      cleanup();
    };
  }, [playbackEnabled, orchestrationActive, playbackGeneration, rootRef]);

  if (!playbackEnabled) {
    return createFinalPlaybackState();
  }

  return state;
}

function useArtboardScale(wrapRef: RefObject<HTMLDivElement | null>): number {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (wrap === null) {
      return;
    }

    const updateScale = (): void => {
      setScale(computeArtboardScale(wrap.clientWidth, wrap.clientHeight));
    };

    updateScale();

    const observer = new ResizeObserver(updateScale);
    observer.observe(wrap);

    return (): void => {
      observer.disconnect();
    };
  }, [wrapRef]);

  return scale;
}

function outcomeStyle(outcome: (typeof DEBATE_STARTS_OUTCOMES)[number]): CSSProperties {
  const { style } = outcome;
  return {
    ...('left' in style ? { left: style.left } : { right: style.right }),
    top: style.top,
    ['--debate-starts-rot' as string]: `${style.rotate}deg`,
  };
}

function chipStyle(chip: (typeof DEBATE_STARTS_CHIPS)[number]): CSSProperties {
  const { style } = chip;
  return {
    ...('left' in style ? { left: style.left } : { right: style.right }),
    ...('top' in style ? { top: style.top } : { bottom: style.bottom }),
  };
}

export function DebateStartsStepAnimation(): JSX.Element {
  const instanceKey = useHowItWorksPlaybackInstanceKey(0);
  return <DebateStartsStepAnimationBody key={instanceKey} />;
}

function DebateStartsStepAnimationBody(): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const scale = useArtboardScale(wrapRef);
  const playback = useDebateStartsPlayback(rootRef);

  const filledColumns = meterFilledColumns(playback.meterFrac);
  const slotWidth = DEBATE_STARTS_ARTBOARD.width * scale;
  const slotHeight = DEBATE_STARTS_ARTBOARD.height * scale;

  const meterDots = METER_DOTS.map((dot) => ({
    ...dot,
    fill: dot.col < filledColumns ? DEBATE_STARTS_METER.fill : DEBATE_STARTS_METER.empty,
  }));

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className="debate-starts relative h-full w-full"
      data-testid="debate-starts-animation"
    >
      <div aria-hidden="true" className="debate-starts-bg" data-testid="debate-starts-background">
        <div className="debate-starts-bg-dots">
          {/* ALLOW-RAW-IMG: static Figma 4427:65 halftone band, not user content */}
          {/* biome-ignore lint/performance/noImgElement: REASON: static local halftone asset positioned via CSS transforms; next/image cannot match the Figma band layout. */}
          <img alt="" className="debate-starts-bg-dots-img" src={DEBATE_STARTS_BACKGROUND_SRC} />
        </div>
        <div className="debate-starts-bg-glow debate-starts-bg-glow-left" />
        <div className="debate-starts-bg-glow debate-starts-bg-glow-right" />
      </div>
      <div ref={wrapRef} className="debate-starts-wrap">
        <div className="debate-starts-slot" style={{ width: slotWidth, height: slotHeight }}>
          <div className="debate-starts-scale" style={{ transform: `scale(${scale})` }}>
            <div
              className={cn(
                'debate-starts-stage',
                playback.go && 'debate-starts-go',
                playback.isCard && 'debate-starts-is-card',
                playback.isCollapsed && 'debate-starts-is-collapsed',
              )}
            >
              <div
                className="debate-starts-surface"
                style={playback.surfaceOpaque ? { opacity: 1 } : undefined}
              >
                <div className="debate-starts-card-clip">
                  <div className="debate-starts-tile">🇺🇸</div>
                  <div className="debate-starts-actions">
                    {/* ALLOW-RAW-BUTTON: decorative aria-hidden animation chrome; not interactive */}
                    <button
                      aria-hidden="true"
                      className="debate-starts-iconbtn"
                      tabIndex={-1}
                      type="button"
                    >
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M4 2 h8 a1 1 0 0 1 1 1 v11 l-5 -3 -5 3 v-11 a1 1 0 0 1 1 -1 z" />
                      </svg>
                    </button>
                    {/* ALLOW-RAW-BUTTON: decorative aria-hidden animation chrome; not interactive */}
                    <button className="debate-starts-sharebtn" tabIndex={-1} type="button">
                      Share
                    </button>
                  </div>
                  <h3 className="debate-starts-title">
                    Was the 2020 U.S. Presidential <br />
                    Election stolen from Trump?
                  </h3>
                  <div className="debate-starts-meter">
                    <svg
                      aria-hidden="true"
                      preserveAspectRatio="none"
                      viewBox={`0 0 ${DEBATE_STARTS_METER.width} ${DEBATE_STARTS_METER.height}`}
                    >
                      {meterDots.map((dot) => (
                        <circle
                          key={`${dot.col}-${dot.row}`}
                          cx={dot.cx}
                          cy={dot.cy}
                          fill={dot.fill}
                          r={DEBATE_STARTS_METER.dotRadius}
                        />
                      ))}
                    </svg>
                  </div>
                  <div className="debate-starts-amount">
                    <span className="debate-starts-amount-main">€{playback.amountEur} / €999</span>{' '}
                    <span className="debate-starts-amount-pct">({playback.pct}%)</span>
                  </div>
                  <div className="debate-starts-divider" />
                  <div className="debate-starts-footer">
                    <div className="debate-starts-avs">
                      <span className="debate-starts-av debate-starts-av-badge">TR</span>
                      <span className="debate-starts-av debate-starts-av-badge">EE</span>
                      <span className="debate-starts-av debate-starts-av-g" />
                      <span className="debate-starts-av debate-starts-av-c" />
                      <span className="debate-starts-av debate-starts-av-r" />
                    </div>
                    <div className="debate-starts-count">
                      Backed by <b>{playback.users}</b> users
                    </div>
                  </div>
                  <div className="debate-starts-live">
                    <span className="debate-starts-live-dot" />
                    Live
                  </div>
                </div>
              </div>

              {DEBATE_STARTS_OUTCOMES.map((outcome, index) => (
                <div
                  key={outcome.id}
                  className={cn(
                    'debate-starts-outcome',
                    playback.visibleOutcomes[index] && 'debate-starts-outcome-visible',
                  )}
                  style={outcomeStyle(outcome)}
                >
                  <span className={cn('debate-starts-odot', outcome.dotClass)} />
                  {outcome.label}
                </div>
              ))}

              {DEBATE_STARTS_CHIPS.map((chip, index) => (
                <div
                  key={chip.name}
                  className={cn(
                    'debate-starts-chip',
                    playback.visibleChips[index] && 'debate-starts-chip-visible',
                  )}
                  style={chipStyle(chip)}
                >
                  <span className={cn('debate-starts-chip-av', chip.avatarClass)} />
                  <span>
                    <span className="debate-starts-chip-name">{chip.name}</span>
                    <br />
                    <span className="debate-starts-chip-amt">{chip.amount}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
